import { useState, useCallback, useEffect, useRef } from 'react'
import EvaluationService from '../../../services/evaluation_service'
import { useTenant } from '../../../hooks/useTenant'

export const useEvaluationDraft = ({
    idActivity,
    selectedTopicId,
    members,
    excludedIds,
    defaultLevelId,
    withLoading,
    activeEventId
}) => {
    const { idTenant } = useTenant()
    const [draftLevelsByTopicId, setDraftLevelsByTopicId] = useState({})
    const prefilledRef = useRef(new Set())
    const selectedTopicIdRef = useRef(selectedTopicId)

    useEffect(() => {
        selectedTopicIdRef.current = selectedTopicId
    }, [selectedTopicId])

    // Carregar avaliações (do Ciclo Atual ou Histórico)
    useEffect(() => {
        // Reset state immediately only if activity or event changes
        setDraftLevelsByTopicId({})
        prefilledRef.current = new Set()
    }, [idTenant, idActivity, activeEventId])

    useEffect(() => {
        if (!idTenant || !idActivity || !members.length) return

        // Identificar alunos que ainda não foram "processados" para carregamento
        const missingMemberIds = members
            .map(c => String(c.id))
            .filter(id => !prefilledRef.current.has(`${idActivity}_${id}`))

        if (missingMemberIds.length === 0) return

        const loadEvaluations = async () => {
            try {
                // 1. Prioridade: Buscar avaliações salvas no CICLO ATUAL
                let currentCycleEvaluations = []
                if (activeEventId) {
                    currentCycleEvaluations = await EvaluationService.getByActivityAndEvent(idTenant, idActivity, activeEventId)
                }

                // 2. Fallback: Buscar últimos níveis (Histórico) para quem não tem avaliação no ciclo atual
                const membersInCurrentCycle = new Set(currentCycleEvaluations.map(e => String(e.idMember)))
                const membersNeedingHistory = missingMemberIds.filter(id => !membersInCurrentCycle.has(id))

                let historyEvaluations = {}
                if (membersNeedingHistory.length > 0) {
                    historyEvaluations = await EvaluationService.getLatestByMemberAndActivity(idTenant, idActivity, membersNeedingHistory)
                }

                setDraftLevelsByTopicId(prev => {
                    const newDraft = { ...prev }

                    // 1. Processar dados do Ciclo Atual (mapeando critérios por tópico) - PRIORITÁRIO
                    currentCycleEvaluations.forEach(evalDoc => {
                        const memberId = String(evalDoc.idMember)
                        if (evalDoc.objectives) {
                            evalDoc.objectives.forEach(obj => {
                                if (obj.topics) {
                                    obj.topics.forEach(t => {
                                        const tId = String(t.id || t.idTopic)
                                        if (!tId || tId === 'undefined') return
                                        if (!newDraft[tId]) newDraft[tId] = {}
                                        newDraft[tId][memberId] = t.idLevel
                                    })
                                }
                            })
                        }
                    })

                    // 2. Processar dados do Histórico (Fallback para quem não tem no ciclo atual)
                    // Percorre todos os alunos que trouxeram histórico
                    Object.entries(historyEvaluations).forEach(([memberId, evalDoc]) => {
                        const cId = String(memberId)

                        // Se houver objetivos detalhados, preenchemos todos os tópicos encontrados
                        if (evalDoc.objectives) {
                            evalDoc.objectives.forEach(obj => {
                                if (obj.topics) {
                                    obj.topics.forEach(t => {
                                        const tId = String(t.id || t.idTopic)
                                        if (!tId || tId === 'undefined') return
                                        if (!newDraft[tId]) newDraft[tId] = {}
                                        // Só aplica se o Ciclo Atual não tiver preenchido nada para esse aluno/tópico
                                        if (!newDraft[tId][cId]) {
                                            newDraft[tId][cId] = t.idLevel
                                        }
                                    })
                                }
                            })
                        }
                        // Removed legacy fallback handling for old formats without objectives
                    })

                    return newDraft
                })

                // Marcar todos como carregados
                missingMemberIds.forEach(id => prefilledRef.current.add(`${idActivity}_${id}`))

            } catch (error) {
                console.error("Erro ao carregar rascunhos de avaliação:", error)
            }
        }

        if (withLoading) withLoading('prefill', loadEvaluations)
        else loadEvaluations()

    }, [idTenant, idActivity, members, activeEventId, withLoading])

    const currentTopicLevels = draftLevelsByTopicId[selectedTopicId] || {}

    const handleLevelChange = useCallback((memberId, levelId) => {
        if (!selectedTopicId) return

        setDraftLevelsByTopicId(prev => ({
            ...prev,
            [selectedTopicId]: {
                ...(prev[selectedTopicId] || {}),
                [memberId]: levelId
            }
        }))
    }, [selectedTopicId])

    const dirtyCount = Object.entries(currentTopicLevels).filter(([memberId, levelId]) => {
        const isExcluded = new Set(excludedIds || []).has(String(memberId))
        return !isExcluded && !!levelId
    }).length

    return {
        draftLevelsByTopicId,
        currentTopicLevels,
        dirtyCount,
        handleLevelChange
    }
}
